import React, { useMemo, useState } from "react";
import { useData, ADMIN_TABLE } from "../../context/DataContext";
import { cellText, normalizeToMs, STATUS_OPTIONS } from "../../utils/helpers";
import TaskTable from "../../components/TaskTable";

// Same list the Create/Edit forms offer, minus "Not Applicable".
const RECURRING_TYPES = ["Daily", "Weekly", "Monthly", "Quarterly", "Half Yearly", "Yearly"];

// A record carries a real recurring type (i.e. it defines a schedule).
// Mirrors the cron's own query: recurring_type != 'Not Applicable' AND != '' AND NOT NULL.
function recurringTypeOf(record) {
  const raw = cellText(record.fields?.["Recurring Type"]).trim();
  if (!raw || raw === "—" || raw.toLowerCase() === "not applicable") return "";
  return raw;
}

// Every generated copy is written by the cron with
// "Recurring ID" = the originating task's Task ID (worker.js processRecurringTasks).
// So all rows belonging to one schedule share this key, and a schedule that has
// never been copied keys on its own Task ID. Grouping on it collapses a whole
// recurring family down to a single row.
function familyKeyOf(record) {
  const f = record.fields || {};
  const recurId = cellText(f["Recurring ID"]).trim();
  const taskId = cellText(f["Task ID"]).trim();
  return recurId || taskId || record.record_id;
}

export default function RecurringTasksAdmin() {
  const { adminRecords, loading } = useData();
  const [typeFilter, setTypeFilter] = useState("All");

  // Every row that declares a recurring type — schedules plus any legacy copies
  // that kept their type instead of being reset to "Not Applicable".
  const recurringRows = useMemo(
    () => adminRecords.filter(r => recurringTypeOf(r) !== ""),
    [adminRecords]
  );

  // One row per recurring task. Within a family we prefer the original schedule
  // (the row whose own Task ID is the family key); if that row is gone, we fall
  // back to the earliest remaining copy so the schedule is never lost entirely.
  const uniqueTasks = useMemo(() => {
    const groups = new Map();
    for (const r of recurringRows) {
      const key = familyKeyOf(r);
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(r);
    }
    const out = [];
    for (const [key, rows] of groups) {
      const original = rows.find(r => cellText(r.fields?.["Task ID"]).trim() === key);
      if (original) { out.push(original); continue; }
      const earliest = rows.slice().sort(
        (a, b) => (normalizeToMs(a.fields?.["Start Date"]) || 0) - (normalizeToMs(b.fields?.["Start Date"]) || 0)
      )[0];
      out.push(earliest);
    }
    return out;
  }, [recurringRows]);

  const countsByType = useMemo(() => {
    const m = {};
    for (const r of uniqueTasks) {
      const t = recurringTypeOf(r);
      m[t] = (m[t] || 0) + 1;
    }
    return m;
  }, [uniqueTasks]);

  // Known types first, then anything unexpected present in the data, so a type
  // added in Lark later stays reachable instead of being silently hidden.
  const chips = useMemo(() => {
    const extras = Object.keys(countsByType)
      .filter(t => !RECURRING_TYPES.some(k => k.toLowerCase() === t.toLowerCase()))
      .sort();
    return ["All", ...RECURRING_TYPES, ...extras];
  }, [countsByType]);

  // Handed to TaskTable, which applies its own search/filters on top. Its Export
  // Excel button exports that final result, so the download always matches the screen.
  const visible = useMemo(() => {
    if (typeFilter === "All") return uniqueTasks;
    return uniqueTasks.filter(r => recurringTypeOf(r).toLowerCase() === typeFilter.toLowerCase());
  }, [uniqueTasks, typeFilter]);

  const collapsed = recurringRows.length - uniqueTasks.length;

  if (loading && adminRecords.length === 0) {
    return <div className="loading-box"><div className="spinner" /><span>Loading recurring tasks...</span></div>;
  }

  return (
    <div className="fade-in">
      <style>
        {`
          .recurring-chip-row {
            display: flex;
            flex-wrap: wrap;
            gap: var(--space-2);
            margin-bottom: var(--space-2);
          }
          .recurring-chip {
            display: inline-flex;
            align-items: center;
            gap: var(--space-1-5);
            padding: var(--space-2) var(--space-3-5);
            min-height: 38px;
            border: 1px solid var(--border-default);
            border-radius: var(--radius);
            background: var(--surface-1);
            color: var(--text-tertiary);
            font-size: var(--text-sm);
            font-weight: 700;
            font-family: inherit;
            white-space: nowrap;
            cursor: pointer;
            box-shadow: var(--shadow-xs);
            transition: var(--transition-fast);
          }
          .recurring-chip:hover {
            background: var(--surface-3);
            color: var(--primary);
          }
          .recurring-chip.active {
            background: var(--primary);
            border-color: var(--primary);
            color: var(--surface-0);
          }
          .recurring-chip .chip-count {
            padding: 1px 7px;
            border-radius: var(--radius-pill);
            background: var(--surface-3);
            color: var(--text-secondary);
            font-size: 11px;
            font-weight: 800;
          }
          .recurring-chip.active .chip-count {
            background: rgba(255, 255, 255, 0.25);
            color: var(--surface-0);
          }
          .recurring-note {
            font-size: 12px;
            color: var(--text-tertiary);
            margin-bottom: 20px;
          }
          @media (max-width: 768px) {
            .recurring-chip {
              flex: 1 1 calc(50% - var(--space-2));
              justify-content: center;
            }
          }
        `}
      </style>

      <div className="recurring-chip-row">
        {chips.map(t => {
          const count = t === "All" ? uniqueTasks.length : (countsByType[t] || 0);
          return (
            <button
              key={t}
              type="button"
              className={`recurring-chip ${typeFilter === t ? "active" : ""}`}
              onClick={() => setTypeFilter(t)}
            >
              {t === "All" ? "🔄 All" : t}
              <span className="chip-count">{count}</span>
            </button>
          );
        })}
      </div>

      <div className="recurring-note">
        Showing <strong>{uniqueTasks.length}</strong> recurring task
        {uniqueTasks.length === 1 ? "" : "s"} — one row per schedule.
        {collapsed > 0 && <> {collapsed} generated cop{collapsed === 1 ? "y was" : "ies were"} merged into the original.</>}
      </div>

      <TaskTable
        title={typeFilter === "All" ? "🔄 Recurring Tasks" : `🔄 Recurring Tasks — ${typeFilter}`}
        records={visible}
        tableId={ADMIN_TABLE}
        canEdit
        canDelete
        emptyMsg={
          typeFilter === "All"
            ? "No recurring tasks set up yet."
            : `No ${typeFilter} recurring tasks found.`
        }
        statusOptionsOverride={STATUS_OPTIONS}
        kanbanLayout="recurringType"
      />
    </div>
  );
}
